import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useLocation } from 'react-router';
import { useNavigationsQuery } from '@data/navigations/useNavigationsQuery';
import { useTenantFeatureFlagsQuery } from '@data/tenants/useTenantFeatureFlagsQuery';
import type { ActiveNavigationNode } from './useActiveNav';
import { linkTargetProps, toHref, useActiveNav } from './useActiveNav';
import { MegaMenu } from './MegaMenu';
import { SearchTrigger } from '../SearchTrigger';
import { GlobalSearch } from '../GlobalSearch';

const SEARCH_PANE_ID = 'global-search-pane';

/**
 * The desktop way into search: the band trigger plus the pane it opens. Extracted so `HeaderNav`
 * stays under the function-length threshold — it is one concern, not two.
 *
 * `paneKey` remounts the pane on close so it does not reopen holding the previous search, the same
 * reset `useDrawerSearch` performs on mobile. The pane returns `null` while closed, but React keeps
 * the element's state alive across that, so a plain conditional is not enough.
 */
function HeaderSearch({ isOpen, onOpenChange }: { isOpen: boolean; onOpenChange: (open: boolean) => void }) {
  const [paneKey, setPaneKey] = useState(0);

  const close = useCallback(() => {
    setPaneKey((previous) => previous + 1);
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <>
      <SearchTrigger
        variant="band"
        label="Search the site"
        expanded={isOpen}
        controls={SEARCH_PANE_ID}
        onClick={() => onOpenChange(true)}
      />
      <GlobalSearch key={paneKey} variant="pane" id={SEARCH_PANE_ID} open={isOpen} onClose={close} focusOnOpen />
    </>
  );
}

// Grace period before a hover-out closes the panel — long enough to cross the
// gap between the nav row and the mega panel without the panel vanishing.
const HOVER_CLOSE_DELAY_MS = 200;

function navItemColorStyle(color: string | null | undefined): CSSProperties | undefined {
  return color ? ({ '--nav-item-color': color } as CSSProperties) : undefined;
}

/**
 * Desktop (wide) header navigation. Items with a mega config render as
 * toggle buttons opening the MegaMenu panel (on click or hover); plain items
 * keep their link + CSS hover dropdown behavior.
 */
export function HeaderNav({ className }: { className?: string }) {
  const { data, isLoading, error } = useNavigationsQuery();
  const location = useLocation();
  const items = useActiveNav(data?.header?.desktopNodes);

  const [openMegaId, setOpenMegaId] = useState<string | null>(null);
  // Plain-item dropdowns are CSS :hover/:focus-within driven, not React state — a
  // clicked child <Link> stays mounted (client-side routing) and keeps its hover/focus,
  // which would otherwise leave the panel visibly open until the pointer moves away.
  // This id force-closes that one dropdown via a CSS override until the item is next
  // hovered or left — NOT cleared on route change: a dropdown <Link> click changes
  // location.pathname in the same tick it sets this, so a pathname-keyed effect would
  // immediately undo the suppression it's meant to apply.
  const [suppressedDropdownId, setSuppressedDropdownId] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // Absent flags mean search is on — the endpoint defaults the same way, so a slow or failed
  // flags request shows the trigger rather than silently removing the feature.
  const { data: featureFlags } = useTenantFeatureFlagsQuery();
  const isSearchEnabled = featureFlags?.globalSearch !== false;
  const navRef = useRef<HTMLElement>(null);
  const anchorRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  // Hovering out of the nav (row + panel — the panel is a nav child) closes
  // the panel after a short grace period; re-entering cancels the timer.
  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setOpenMegaId(null);
    }, HOVER_CLOSE_DELAY_MS);
  }, [cancelScheduledClose]);

  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  // Close the panel on route change. The search pane goes with it: clicking a result navigates,
  // and a pane left open over the page you just asked for reads as the click not having worked.
  useEffect(() => {
    setOpenMegaId(null);
    setIsSearchOpen(false);
  }, [location.pathname]);

  // Close on Escape / outside pointerdown while open
  useEffect(() => {
    if (!openMegaId) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenMegaId(null);
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenMegaId(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [openMegaId]);

  if (isLoading || error || items.length === 0) {
    return null;
  }

  const openItem = items.find((item) => item.id === openMegaId && item.mega);
  const navClassName = className ? `nav ${className}` : 'nav';

  return (
    <nav className={navClassName} ref={navRef} onMouseEnter={cancelScheduledClose} onMouseLeave={scheduleClose}>
      <ul className="nav__list">
        {items.map((item) => (
          <li
            className="nav__list-item"
            key={item.id}
            onMouseLeave={() => setSuppressedDropdownId((prev) => (prev === item.id ? null : prev))}
            // Covers keyboard-only re-entry (Tab back into this item): mouseLeave/onHover
            // never fire without a pointer, so without this a suppressed dropdown would
            // stay force-closed for the rest of the session once focus leaves and returns.
            onFocus={() => setSuppressedDropdownId((prev) => (prev === item.id ? null : prev))}
          >
            <NavItem
              item={item}
              isOpen={openMegaId === item.id}
              isDropdownSuppressed={suppressedDropdownId === item.id}
              onToggle={() => setOpenMegaId(openMegaId === item.id ? null : item.id)}
              onHover={() => {
                setOpenMegaId(item.mega ? item.id : null);
                // A fresh hover/focus is a new intent to open the dropdown — release
                // any earlier click-suppression so it isn't stuck closed all session.
                setSuppressedDropdownId((prev) => (prev === item.id ? null : prev));
              }}
              onDropdownNavigate={() => setSuppressedDropdownId(item.id)}
              anchorRef={(el) => {
                anchorRefs.current[item.id] = el;
              }}
            />
          </li>
        ))}
      </ul>
      {isSearchEnabled && <HeaderSearch isOpen={isSearchOpen} onOpenChange={setIsSearchOpen} />}
      {openItem?.mega && (
        <MegaMenu
          config={openItem.mega}
          anchorEl={anchorRefs.current[openItem.id] as HTMLButtonElement | null}
          onNavigate={() => setOpenMegaId(null)}
        />
      )}
    </nav>
  );
}

type NavItemProps = {
  item: ActiveNavigationNode;
  isOpen: boolean;
  /** True while this item's plain CSS dropdown is force-closed after a link click. */
  isDropdownSuppressed: boolean;
  onToggle: () => void;
  /** Fired on pointer hover: opens the item's mega, or closes any open one. */
  onHover: () => void;
  /** Fired when a link inside the plain CSS dropdown is clicked. */
  onDropdownNavigate: () => void;
  anchorRef: (el: HTMLButtonElement | null) => void;
};

function NavItem({
  item,
  isOpen,
  isDropdownSuppressed,
  onToggle,
  onHover,
  onDropdownNavigate,
  anchorRef,
}: NavItemProps) {
  const activeClass = item.isActive ? ' nav__link--active' : '';

  if (item.mega) {
    return (
      <button
        ref={anchorRef}
        className={`nav__link small-text nav__toggle${activeClass}${isOpen ? ' nav__toggle--open' : ''}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={onToggle}
        onMouseEnter={onHover}
      >
        {item.label}
      </button>
    );
  }

  return (
    <>
      {item.url ? (
        <Link
          className={`nav__link small-text${activeClass}`}
          style={navItemColorStyle(item.color)}
          to={toHref(item.url)}
          {...linkTargetProps(item.sameTab)}
          onMouseEnter={onHover}
        >
          {item.label}
        </Link>
      ) : (
        <span
          className={`nav__link small-text${activeClass}`}
          style={navItemColorStyle(item.color)}
          onMouseEnter={onHover}
        >
          {item.label}
        </span>
      )}
      {item.children && item.children.length > 0 && (
        <div className={`nav__dropdown${isDropdownSuppressed ? ' nav__dropdown--closed' : ''}`}>
          {item.children.map((child) =>
            child.url ? (
              <Link
                key={child.id}
                className={`nav__dropdown-link small-text${child.isActive ? ' nav__dropdown-link--active' : ''}`}
                to={toHref(child.url)}
                {...linkTargetProps(child.sameTab)}
                onClick={(event) => {
                  // Blur first, before the --closed class hides this subtree — moving
                  // focus ourselves keeps it predictable instead of letting display:none
                  // yank it out from under a keyboard user (WCAG 2.4.7).
                  event.currentTarget.blur();
                  onDropdownNavigate();
                }}
              >
                {child.label}
              </Link>
            ) : (
              <span key={child.id} className="nav__dropdown-link small-text">
                {child.label}
              </span>
            )
          )}
        </div>
      )}
    </>
  );
}
